import React, { useState } from "react";
import {
    Box,
    Collapse,
    Grid,
    IconButton,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import { useAppState } from "../application/AppState";
import useTranslation from "../application/translation";
import { formatDateAccordingToLanguage } from "../application/utils";
import { getInsuranceDocument } from "../application/api";
import PresaleDocs from "./PresaleDocs";

const useStyles = makeStyles({
    tableWrapper: {
        width: "100%",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
    },
    table: {
        minWidth: 650,
    },
    root: {
        "& > *": {
            borderBottom: "unset"
        }
    },
    pointer: {
        "&:hover": {
            cursor: "pointer"
        }
    }
});

const MyInsurances = () => {
    const classes = useStyles();
    const [{ insurances, language }] = useAppState();
    const translation = useTranslation();
    const [openRow, setOpenRow] = useState("");

    return (
        <div>
            <Typography variant="body2">{translation.myins_infotext}</Typography>
            <br />
            <div className={classes.tableWrapper}> 
                <Table className={`${classes.table} insurance`} aria-label="Insurances Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>{translation.myins_docs_showhide}</TableCell>
                            <TableCell>{translation.myins_ins}</TableCell>
                            <TableCell>{translation.myins_card}</TableCell>
                            <TableCell>{translation.myins_fromdate}</TableCell>
                            <TableCell>{translation.myins_todate}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {insurances.map((insurance, index) => (
                            <Row
                                key={insurance.insuranceId}
                                row={insurance}
                                language={language}
                                open={insurance.policyType === openRow}
                                onToggle={() => setOpenRow(openRow === insurance.policyType ? "" : insurance.policyType)}
                            />
                        ))}
                    </TableBody>
                </Table>
                <PresaleDocs />
            </div>
        </div>
    );
};

const decodeBase64ToByteArray = (b64) => {
    const binary = atob(b64);
    return new Uint8Array([...binary].map(char => char.charCodeAt(0)));
};

const downloadInsuranceDocument = async (docId) => {
    const response = await getInsuranceDocument(docId);
    if (!response.ok) return;

    const { fileContents, fileDownloadName, contentType } = await response.json();
    const blob = new Blob([decodeBase64ToByteArray(fileContents)], { type: contentType });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", fileDownloadName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

const Row = ({ row, language, open, onToggle }) => {
    const classes = useStyles();
    const translation = useTranslation();
    const [openModule, setOpenModule] = useState(false);

    const handleDocClick = (e, docId) => {
        e.preventDefault();
        downloadInsuranceDocument(docId);
    };

    return (
        <>
            <TableRow className={classes.root}>
                <TableCell>
                    <IconButton
                        aria-label={
                            openModule
                                ? translation.myins_docs_hide
                                : translation.myins_docs_show
                        }
                        title={
                            openModule
                                ? translation.myins_docs_hide
                                : translation.myins_docs_show
                        }
                        size="small"
                        onClick={() => setOpenModule(!openModule)}
                    >
                        {openModule ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{row.policyType}</TableCell>
                <TableCell>{row.cardLoanName}</TableCell>
                <TableCell>{formatDateAccordingToLanguage(row.startDate, language)}</TableCell>
                <TableCell>{formatDateAccordingToLanguage(row.endDate, language)}</TableCell>
            </TableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={openModule} timeout="auto" unmountOnExit>
                        <Box margin={1}>
                            <Typography variant="caption" gutterBottom>
                                {translation.myins_docs_header}
                            </Typography>
                            <Table size="small" aria-label="Documents Table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>{translation.myins_docs_filename}</TableCell>
                                        <TableCell>{translation.myins_docs_filetype}</TableCell>
                                        <TableCell>{translation.myins_docs_created}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {row.insuranceDocuments?.map((doc) => (
                                        <TableRow key={doc.docId}>
                                            <TableCell />
                                            <TableCell>
                                                <Link
                                                    onClick={(e) => handleDocClick(e, doc.docId)}
                                                    aria-label={`Ladda ner ${doc.fileName}`}
                                                    className={classes.pointer}
                                                >
                                                    {doc.fileName}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{doc.fileType}</TableCell>
                                            <TableCell>{formatDateAccordingToLanguage(doc.created, language)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export default MyInsurances;
